import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Badge } from 'shared/ui/badge'
import { GitBranch } from 'lucide-react'
import type { WorkflowMetadata, WorkflowRuleset, WorkflowStage } from 'modules/admin/types/workflowTypes'

interface WorkflowPreviewPanelProps {
  metadata: WorkflowMetadata
  stages: WorkflowStage[]
  rulesets: WorkflowRuleset[]
}

export function WorkflowPreviewPanel({ metadata, stages, rulesets }: WorkflowPreviewPanelProps) {
  const renderMissingApproverBadge = (value?: string | null) => {
    const normalized = (value ?? 'FAIL').trim().toUpperCase()
    const className =
      normalized === 'SKIP'
        ? 'border-amber-200 bg-amber-50 text-amber-700'
        : 'border-rose-200 bg-rose-50 text-rose-700'

    return (
      <Badge variant="outline" className={className}>
        Missing Approver: {normalized}
      </Badge>
    )
  }

  return (
    <Card className="border border-gray-200">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base font-semibold text-gray-900">
          <GitBranch className="h-4 w-4 text-primary" />
          Workflow Preview
        </CardTitle>
        <p className="text-sm text-gray-500">สรุปลำดับขั้นตอน พร้อม mapping ruleset ไปยัง CAP stage</p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border border-gray-100 bg-gray-50 p-3 text-sm text-gray-700">
          <p className="font-semibold text-gray-900">{metadata.workflowName}</p>
          <p>Code: {metadata.workflowCode}</p>
          <p>Version: {metadata.version}</p>
          <p>Status: {metadata.isActive ? 'ACTIVE' : 'INACTIVE'}</p>
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Stages</p>
          {stages.map((stage) => (
            <div
              key={stage.stageId}
              className="flex items-center justify-between rounded-md border border-gray-100 bg-white p-3"
            >
              <div>
                <p className="text-sm font-semibold text-gray-900">
                  {stage.seqOrder}. {stage.name}
                </p>
                <p className="text-xs text-gray-500">{stage.roleName ?? stage.roleId ?? 'Unassigned role'}</p>
                <p className="text-xs text-gray-500">
                  Status: {stage.statusName ?? stage.statusCode ?? 'ยังไม่กำหนด'}
                </p>
              </div>
              <div className="flex gap-2">
                {stage.isGate ? <Badge variant="default">GATE</Badge> : null}
                {renderMissingApproverBadge(stage.onMissingApprover)}
              </div>
            </div>
          ))}
        </div>
        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Ruleset Routing</p>
          {rulesets.map((ruleset) => {
            const capStage = stages.find((item) => item.stageId === ruleset.capStageId)
            const excludedStages = (ruleset.excludeStageIds ?? [])
              .map((stageId) => stages.find((item) => item.stageId === stageId))
              .filter((item): item is WorkflowStage => Boolean(item))

            return (
              <div key={ruleset.workflowRulesetId} className="rounded-md border border-gray-100 bg-white p-3">
                <p className="text-sm font-semibold text-gray-900">{ruleset.name}</p>
                <p className="text-xs text-gray-500">{ruleset.description}</p>
                {capStage ? (
                  <p className="text-xs text-gray-600">
                    CAP Stage:{' '}
                    <span className="font-semibold text-primary">
                      {capStage.seqOrder}. {capStage.name}
                    </span>
                  </p>
                ) : (
                  <p className="text-xs text-red-500">ยังไม่ได้เลือก CAP stage</p>
                )}
                {excludedStages.length > 0 ? (
                  <p className="mt-1 text-xs text-gray-600">
                    Exclude Stages:{' '}
                    <span className="font-semibold text-slate-900">
                      {excludedStages.map((item) => `${item.seqOrder}. ${item.name}`).join(', ')}
                    </span>
                  </p>
                ) : null}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
